/**
 * URL Context Utilities
 *
 * Extracts contextual IDs from the current URL path for Sentry context tracking.
 * Used by SentryClientContext to set binder.id tag on error reports.
 */

/**
 * Regex patterns for extracting binder ID from URL paths.
 * Client routes: /tax-binders/:binderId/*
 * Preparer routes: /preparer/binders/:binderId/* and /preparer/binder/:binderId/*
 * Legacy client routes (redirect target still in flight): /binders/:binderId/*
 */
const BINDER_ID_PATTERNS = [
  /^\/tax-binders\/([a-f0-9-]{36})/i,
  /^\/binders\/([a-f0-9-]{36})/i,
  /^\/preparer\/binders\/([a-f0-9-]{36})/i,
  /^\/preparer\/binder\/([a-f0-9-]{36})/i,
];

/**
 * Extracts the binder ID from the current URL path.
 *
 * @returns The binder ID (UUID) if found in the URL, or null otherwise.
 *
 * @example
 * // URL: /tax-binders/123e4567-e89b-12d3-a456-426614174000/documents
 * getBinderIdFromUrl() // => '123e4567-e89b-12d3-a456-426614174000'
 *
 * // URL: /documents
 * getBinderIdFromUrl() // => null
 */
export function getBinderIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const path = window.location.pathname;

  for (const pattern of BINDER_ID_PATTERNS) {
    const match = path.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}
